import { useCallback, useMemo, useState } from 'react';
import { useAppSelector } from 'hooks';

import Header from 'components/header.js';
import Footer from 'components/footer.js';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import OPWBreadcrumb from 'components/breadcrumb.js';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import DatePicker from 'react-datepicker';

import { FileWithPath, useDropzone } from 'react-dropzone';
import { AiOutlineWarning, AiOutlineCheckCircle } from 'react-icons/ai';
import { actionUploadYardWtFile } from './actions';
import Alert from 'react-bootstrap/Alert';
import { AxiosProgressEvent } from 'axios';
import { useLocation } from 'react-router-dom';
import { Required, SessionName } from 'pages/sessionManagement/controls';
import {
	NewSessionMode,
	SessionDetailsModel,
	useSessionDetailsModel,
} from 'pages/sessionManagement/sessionDetailsModel';
import { QueryClient, QueryClientProvider } from 'react-query';
import { CheckAuth } from 'pages/auth/CheckAuth';
import { GenericListSelect } from 'components/genericListSelect';
import { GenericDictSelect } from 'components/genericDictSelect';
import { Button, ButtonGroup, Form, ProgressBar } from 'react-bootstrap';
import { GenericModal } from 'components/genericModal';
import Loading from 'components/loading';

const queryClient = new QueryClient({
	defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

const DROP_BOX_STYLE = {
	margin: '1rem',
	padding: '2rem',
	border: '3px #ddd dashed',
	borderRadius: '0.5rem',
	maxWidth: '500px',
	color: '#575757',
};
const COLS = {
	lhs: 4,
	rhs: 8,
};

const UploadBox = (props: { onFileDropped: (f: FileWithPath) => void }) => {
	const onDrop = (acceptedFiles: FileWithPath[]) => {
		if (acceptedFiles.length === 1) props.onFileDropped(acceptedFiles[0]);
	};

	const { getRootProps, getInputProps, isDragActive, isDragAccept } =
		useDropzone({
			maxFiles: 1,
			accept: {
				'text/csv': ['.csv'],
			},
			onDrop,
		});

	const message = useMemo(() => {
		if (!isDragActive)
			return 'Click here or drag-n-drop the weights file (CSV).';
		if (isDragAccept)
			return (
				<>
					<AiOutlineCheckCircle /> Drop here
				</>
			);
		return (
			<>
				<AiOutlineWarning />
				Only a single CSV file can be uploaded
			</>
		);
	}, [isDragActive, isDragAccept]);

	return (
		<div {...getRootProps()} style={DROP_BOX_STYLE}>
			<input {...getInputProps()} />
			{message}
		</div>
	);
};

const WeightsUploadForm = () => {
	const location = useLocation();
	const clientId = useAppSelector((state) => state.clientId);
	const clientIds = useAppSelector((state) => state.clientIds);
	const [fileUploadSuccess, setFileUploadSuccess] = useState(0);
	const [uploadProgress, setUploadProgress] = useState(0);

	const currentAccount = clientIds?.find(
		(account) => account.ClientID === clientId
	);

	const model = useSessionDetailsModel(
		location.state?.edit,
		NewSessionMode.Upload
	);

	const uploadProgressEvent = (e: AxiosProgressEvent) => {
		const progress = e.total ? (e.loaded / e.total) * 100 : 50;
		setUploadProgress(progress);
	};

	const fileUploadAction = useCallback(
		(sessionId: number) => {
			if (!clientId || !model.uploadFile) return;

			setFileUploadSuccess(0);
			actionUploadYardWtFile(
				model.uploadFile,
				model.uploadFile.name,
				clientId,
				sessionId,
				uploadProgressEvent
			).then((uploadResult) => {
				if (uploadResult.success) {
					setFileUploadSuccess(1);
				} else {
					setFileUploadSuccess(2);
				}
			});
		},
		[clientId, model]
	);

	return (
		<>
			<Row className="inputRow">
				<Col sm={COLS.lhs}>Account:</Col>
				<Col sm={COLS.rhs}>
					<span className="fixed-form-value">
						{currentAccount?.TradingName} [id:{currentAccount?.ClientID}]
					</span>
				</Col>
			</Row>
			<SessionName
				cols={COLS}
				required={true}
				onChange={model.name.set}
				value={model.name.value}
				watermark="For your reference"
			/>
			<Row className="inputRow">
				<Col
					sm={COLS.lhs}
					style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
				>
					Date:
				</Col>
				<Col sm={COLS.rhs}>
					<DatePicker
						className={'datepickerxx form-control'}
						dateFormat={'dd MMM yyyy'}
						selected={model.startTime.value}
						showTimeSelect={false}
						todayButton={'Now'}
						scrollableYearDropdown={true}
						showYearDropdown={true}
						onChange={(date: Date) => {
							if (model.startTime.set) model.startTime.set(date);
						}}
						placeholderText={'Leave blank if date is in your file'}
					/>
				</Col>
			</Row>
			{(model.timezone.value !== undefined || model.timezone.set) && (
				<GenericListSelect
					id="timezone"
					label="Timezone"
					dataArray={model.timezone.options}
					cols={COLS}
					handleChange={(evt: any) => {
						if (model.timezone.set) model.timezone.set(evt.target.value);
					}}
					value={model.timezone.value}
					required={!!model.timezone.set}
					invalidMsg="Select a time zone"
					lock={!model.timezone.set}
				/>
			)}
			{model.speciesSelect && (
				<GenericDictSelect
					cols={COLS}
					label="Species"
					dataDict={model.speciesSelect?.options}
					value={model.speciesSelect.value ?? 0}
					handleChange={(evt: any) =>
						model.speciesSelect?.set!(evt.target.value)
					}
					required={true}
					placeholder="Leave blank if species is in your file"
					selectDotDotDot={false}
					lock={!model.speciesSelect.set}
				/>
			)}
			{model.breed?.options ? (
				<GenericListSelect
					id="BreedType"
					label="Breed"
					dataArray={model.breed.options}
					cols={COLS}
					handleChange={(evt: any) => {
						if (model.breed?.set) model.breed.set(evt.target.value);
					}}
					value={model.breed.value}
					required={false}
					invalidMsg="Select a breed type."
					placeholder="Leave blank if breed is in your file"
					lock={!model.breed.set}
				/>
			) : (
				model.breed && (
					<Row className="inputRow">
						<Col sm={COLS.lhs}>Breed:</Col>
						<Col sm={COLS.rhs}>
							<Form.Control
								id="BreedType"
								placeholder="Leave blank if breed is in your file"
								required={true}
								onChange={(evt) => {
									if (model.breed?.set) model.breed.set(evt.target.value);
								}}
								value={model.breed?.value ?? ''}
								disabled={!model.breed.set}
							/>
						</Col>
					</Row>
				)
			)}
			{model.sex && (
				<GenericDictSelect
					id="Sex"
					label="Sex"
					required={false}
					dataDict={model.sex.options}
					cols={COLS}
					handleChange={(evt: any) => {
						if (model.sex?.set) model.sex.set(evt.target.value);
					}}
					value={model.sex.value}
					invalidMsg="Select animal sex."
					selectDotDotDot={true}
					placeholder="Leave blank if sex is in your file"
					loading={false}
					lock={!model.sex.set}
				/>
			)}
			{model.age && (
				<GenericListSelect
					id="Age"
					label="Age group"
					dataArray={model.age.options}
					cols={COLS}
					handleChange={(evt: any) => {
						if (model.age?.set) model.age.set(evt.target.value);
					}}
					value={model.age.value}
					required={true}
					invalidMsg="Select animal age"
					placeholder="Select the avg age of the weighed animals"
				/>
			)}
			{model.woolGrowth?.set && (
				<GenericDictSelect
					cols={COLS}
					controlStyle={{ width: '15em' }}
					required={true}
					label="Wool growth"
					dataDict={model.woolGrowth.options}
					selectDotDotDot={true}
					handleChange={(evt: any) => model.woolGrowth!.set!(evt.target.value)}
					value={model.woolGrowth.value}
				/>
			)}
			{model.adjustType && (
				<GenericDictSelect
					cols={COLS}
					label="Weight type"
					dataDict={model.adjustType?.options}
					value={model.adjustType.value ?? 0}
					handleChange={(evt: any) => model.adjustType?.set!(evt.target.value)}
					required={true}
					excludeKeys="0"
					selectDotDotDot={true}
				/>
			)}
			<Row className="inputRow">
				<Col
					sm={COLS.lhs}
					style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
				>
					Muster time:
					<Required required={true} />
				</Col>
				<Col sm={COLS.rhs}>
					<Form.Control
						id="MusterTime"
						type="number"
						placeholder="Hours from muster to weighing"
						onChange={(evt) => {
							if (model.musterTime?.set)
								model.musterTime.set(parseInt(evt.target.value));
						}}
						value={model.musterTime?.value ?? ''}
					/>
				</Col>
			</Row>
			<GenericListSelect
				id="Feed"
				label="Feed type"
				dataArray={model.feedType?.options}
				cols={COLS}
				handleChange={(evt: any) => {
					if (model.feedType?.set) model.feedType.set(evt.target.value);
				}}
				value={model.feedType?.value}
				required={true}
				invalidMsg="Select feed type"
				placeholder="The type of feed the stock were most recently on"
			/>

			{model.setUploadFile && (
				<div className="inputRow row">
					<Col sm={COLS.lhs + COLS.rhs}>
						{fileUploadSuccess === 2 ? (
							<Alert variant={'danger'}>
								<p style={{ textAlign: 'center' }}>
									File upload failed. Try again or contact Optiweigh Support.
								</p>
							</Alert>
						) : fileUploadSuccess === 1 ? (
							<Alert variant={'success'}>
								<p style={{ textAlign: 'center' }}>
									✅ File has been uploaded successfully.
								</p>
							</Alert>
						) : model.uploadFile ? (
							<div style={DROP_BOX_STYLE}>
								<ProgressBar
									animated={uploadProgress < 100}
									now={uploadProgress}
								/>
								{uploadProgress > 0 ? (
									'Uploading ' + model.uploadFile.name
								) : (
									<>
										File selected:
										<br />✅ <strong>{model.uploadFile.name}</strong>
									</>
								)}
							</div>
						) : (
							<>
								<p>Upload your data file to the box below:</p>
								<UploadBox onFileDropped={model.setUploadFile} />
								<p>
									Excel or CSV file from any weighing indicator or from other
									sources such as management systems or feedlot/abattoir
									feedback sheets.
								</p>
							</>
						)}
					</Col>
				</div>
			)}

			{model.errorMsg && (
				<Row className="inputRow">
					<Col sm={COLS.lhs + COLS.rhs}>
						<Alert variant="warning">{model.errorMsg}</Alert>
					</Col>
				</Row>
			)}
			{model.modalMessage && <GenericModal {...model.modalMessage} />}
			<Row className="inputRow">
				<Col sm={COLS.lhs}></Col>
				<Col sm={COLS.rhs}>
					<Loading loading={model.isLoading} />
				</Col>
			</Row>
			<Row className="inputRow">
				<Col sm={COLS.lhs}></Col>
				<Col sm={COLS.rhs}>
					<Controls
						model={model}
						disabled={model.isLoading}
						fileUploadAction={fileUploadAction}
					/>
				</Col>
			</Row>
		</>
	);
};

const Controls = (props: {
	model: SessionDetailsModel;
	disabled?: boolean;
	fileUploadAction?: Function;
}) => {
	const { onSubmit, onCancel } = props.model;
	const fileUploadAction = props.fileUploadAction;

	const processForm = () => {
		if (onSubmit) {
			const submitResult = onSubmit(fileUploadAction);
			console.debug(`submitResult: ${submitResult}`);
			console.debug(submitResult);
			return submitResult;
		}
	};

	return (
		<div className="formButtons">
			<ButtonGroup>
				<Button
					type="submit"
					variant="outline-primary"
					title="click to save changes"
					{...((props.disabled || !onSubmit) && {
						disabled: true,
						variant: 'outline-secondary',
					})}
					onClick={processForm}
				>
					Save changes
				</Button>
				<Button
					variant="outline-primary"
					onClick={onCancel}
					{...(props.disabled && { disabled: true })}
				>
					Exit without saving
				</Button>
			</ButtonGroup>
		</div>
	);
};

export const WeightsUpload = () => {
	return (
		<QueryClientProvider client={queryClient}>
			<CheckAuth groupName={'admin'} />
			<Header>
				<OPWBreadcrumb trail='[["Home", "/"], ["Weights upload", ""]]' />
			</Header>
			<Container fluid className="body">
				<Card body>
					<Card.Title>Upload Yard or Sale Weights</Card.Title>
					<Card.Body>
						<p>
							This facility allows you to upload animal information such as:
							<ul>
								<li>Visual ID's to show on the individual animal reports;</li>
								<li>
									Weight records for inclusion in weight gain calculations; and
								</li>
								<li>
									Weight records to be used to compare your Optiweigh data with
									your yard weights or your sale weights.
								</li>
							</ul>
						</p>
						<WeightsUploadForm />
					</Card.Body>
				</Card>
			</Container>
			<Container fluid>
				<Row>
					<Col sm="12">
						<Footer />
					</Col>
				</Row>
			</Container>
		</QueryClientProvider>
	);
};
